import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { OAuth2Tokens, TokenStorage } from '@forgerock/javascript-sdk';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, map, Observable, of, take, tap, timeout } from 'rxjs';

import { env } from '@amfam/environment';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import {
  selectIsPersonalAccount,
  selectUserLoaded
} from '@amfam/shared/user/lib/+state/user.selectors';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { LOCAL_STORAGE_KEYS } from '@amfam/shared/utility/shared-services/lib/storage/storage-keys';
import {
  formatErrorMessage,
  getErrorMessage
} from '@amfam/shared/utility/shared-services/lib/utils/error.util';
import { LoadingSpinnerService } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner.service';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { forgerockLogin } from '../store/session/forgerock-session.actions';
import { loginUserFail, loginUserSuccess } from '../store/session/session.actions';
import { ForgerockAuthService } from './forgerock-auth.service';

@Injectable({ providedIn: 'root' })
// Exported for the spec, which exercises this class directly with its dependencies mocked.
// The route config injects it through `forgerockAuthGuard` below and should not reference it.
export class ForgerockAuthGuardService {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private router = inject(Router);
  private forgerockAuthService = inject(ForgerockAuthService);
  private analyticsFacade = inject(AnalyticsFacade);
  private spinner = inject(LoadingSpinnerService);
  private copyService = inject(CopyService);
  private componentViewService = inject(ComponentViewService);
  private utilService = inject(UtilService);

  async canActivateRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const url = state.url;

    // Deep link from mobile webview
    const webview = this.componentViewService.isInWebview(url);
    if (webview) {
      this.sendToApp({
        event: 'track',
        message: `[Webview] Can Activate url: ${this.utilService.maskSensitiveParams(url)}`
      });
      this.sendToApp({ event: 'track', message: `[Webview] Inside webview` });
      const userId = this.getUserId(route);
      const authCtx = this.getAuthCtx(route);
      this.sendToApp({
        event: 'track',
        message: `[Webview] UserId : ${userId} | Auth Context: ${authCtx}`
      });

      if (userId) {
        const goTo = encodeURIComponent(
          this.removeQueryParams(`${window.location.origin}${url}`, ['userId', 'authCtx'])
        );
        this.sendToApp({
          event: 'track',
          message: `[Webview] set the go to url in local storage - ${this.utilService.maskSensitiveParams(decodeURIComponent(goTo))}`
        });

        localStorage.setItem(LOCAL_STORAGE_KEYS.GO_TO_URL, decodeURIComponent(goTo));
        const href = `${this.forgerockAuthService.getPartnerUrl()}XUI/?realm=/alpha&authIndexType=service&authIndexValue=Login&goto=${goTo}&userId=${userId}&authCtx=${authCtx}`;

        this.sendToApp({
          event: 'track',
          message: `[Webview] Forgerock href : ${this.utilService.maskSensitiveParams(href)}`
        });

        this.sendToApp({
          event: 'track',
          message: `[Webview] Redirect to forgerock`
        });
        window.location.href = href;
        return false;
      } else {
        this.sendToApp({
          event: 'track',
          message: `[Webview] Back to myaccount with session. url : ${this.utilService.maskSensitiveParams(url)}`
        });

        this.sendToApp({
          event: 'track',
          message: `[Webview] Is Authenticated!`
        });
        const isLoggedIn = await firstValueFrom(
          this.forgerockAuthService.userLoaded$.pipe(
            timeout(20000),
            catchError(error => {
              this.utilService.error(
                `[Auth][Webview] userLoaded$ timeout or error. -- Error: ${getErrorMessage(error)}`,
                {
                  error: formatErrorMessage(error, true)
                }
              );
              return of(false);
            })
          )
        );
        if (isLoggedIn) {
          this.sendToApp({
            event: 'track',
            message: `[Auth][Webview] Authenticated!`
          });
          return true;
        }

        this.forgerockAuthService.redirectUrl = url;
        const goToUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.GO_TO_URL);
        this.sendToApp({
          event: 'track',
          message: `[Webview] Fetching go to url!`
        });
        if (goToUrl) {
          this.sendToApp({
            event: 'track',
            message: `[Webview] go to url found - ${this.utilService.maskSensitiveParams(goToUrl)}`
          });
          localStorage.removeItem(LOCAL_STORAGE_KEYS.GO_TO_URL);
          const urlToRedirect = this.getPath(goToUrl);
          this.sendToApp({
            event: 'track',
            message: `[Webview] Go to url path - ${this.utilService.maskSensitiveParams(urlToRedirect)}`
          });
          if (urlToRedirect !== url) {
            this.router.navigateByUrl(urlToRedirect);
            return false;
          }
        }
        const acrValues = env.forgerock.login;
        this.sendToApp({
          event: 'track',
          message: `[Webview] Try to get tokens after back with session`
        });
        const tokens = await this.forgerockAuthService.getTokens(acrValues, false);
        if (tokens) {
          this.sendToApp({
            event: 'track',
            message: `[Webview] Got tokens - ${tokens}`
          });

          this.sendToApp({
            event: 'track',
            message: `[Webview] Dispatch to authenticate with session`
          });

          // Wait for authentication to complete before allowing navigation
          return firstValueFrom(
            this.dispatchToAuthenticate().pipe(
              timeout(20000),
              tap(() => {
                this.sendToApp({
                  event: 'track',
                  message: `[Webview] Authenticated`
                });
              }),
              catchError(error => {
                this.utilService.error(
                  `[Auth][Webview] Authentication timeout or error. -- Error: ${getErrorMessage(error)}`,
                  {
                    error: formatErrorMessage(error, true)
                  }
                );
                this.spinner.stop();
                this.sendToApp({
                  event: 'track',
                  message: `[Webview] Authentication failed: ${error.message}`
                });
                return of(false);
              })
            )
          );
        }
        return false;
      }
    }

    return this.guard(url);
  }

  removeQueryParams(url: string, paramsToRemove: string[]): string {
    try {
      const urlObj = new URL(url);
      paramsToRemove.forEach(param => {
        urlObj.searchParams.delete(param);
      });
      return urlObj.toString();
    } catch (error) {
      this.utilService.error(
        `[Auth] Invalid URL in removeQueryParams. -- Error: ${getErrorMessage(error)}`,
        {
          error: formatErrorMessage(error, true)
        }
      );
      return url;
    }
  }

  getPath(url: string): string {
    try {
      const urlObj = new URL(url);
      return `${urlObj.pathname}${urlObj.search}`;
    } catch (error) {
      this.utilService.error(`[Auth] Invalid URL in getPath. -- Error: ${getErrorMessage(error)}`, {
        error: formatErrorMessage(error, true)
      });
      return url;
    }
  }

  private getUserId(route: ActivatedRouteSnapshot) {
    return route.queryParamMap.get('userId');
  }

  private getAuthCtx(route: ActivatedRouteSnapshot) {
    return route.queryParamMap.get('authCtx');
  }

  private checkFlow(path: string | null, flow: string) {
    if (!path) return false;
    return path.includes(flow);
  }

  private changePassword(url: string) {
    this.utilService.log(
      `[Auth] Change Password Flow -- ${this.utilService.maskSensitiveParams(url)}`
    );
    if (url.includes('/profile')) return true;
    else {
      this.store.dispatch(
        fromRouterActions.Go({
          path: ['/profile'],
          query: { openAccordion: 'security' }
        })
      );
      return false;
    }
  }

  private paperlessSignup(url: string, personal: boolean) {
    this.utilService.log(
      `[Auth] Paperless Signup Flow -- ${this.utilService.maskSensitiveParams(url)}`
    );
    if (personal) {
      if (url.includes('/paperless/sign-up')) return true;
      else {
        this.store.dispatch(fromRouterActions.Go({ path: ['/paperless/sign-up'] }));
        return false;
      }
    }
    return true;
  }

  private async isAuthenticated() {
    try {
      this.utilService.log('[Auth] Inside isAuthenticated');
      let isUserLoaded = false;
      const tokens = await this.getTokens();
      if (tokens) {
        isUserLoaded = await firstValueFrom(
          this.store.select(selectUserLoaded).pipe(
            timeout(20000),
            catchError(error => {
              this.utilService.error(
                `[Auth] selectUserLoaded timeout or error. -- Error: ${getErrorMessage(error)}`,
                {
                  error: formatErrorMessage(error, true)
                }
              );
              return of(false);
            })
          )
        );
        if (isUserLoaded) {
          return true;
        } else {
          const authenticated = await firstValueFrom(
            this.dispatchToAuthenticate().pipe(
              timeout(20000),
              catchError(error => {
                this.utilService.error(
                  `[Auth] Authentication timeout or error. -- Error: ${getErrorMessage(error)}`,
                  {
                    error: formatErrorMessage(error, true)
                  }
                );
                this.spinner.stop();
                return of(false);
              })
            )
          );
          return authenticated;
        }
      }
    } catch (error) {
      this.utilService.error(
        `[Auth] Error in isAuthenticated. -- Error: ${getErrorMessage(error)}`,
        {
          error: formatErrorMessage(error, true)
        }
      );
      this.spinner.stop();
    }

    return false;
  }

  private async isPersonalAccount() {
    const personalAccount = await firstValueFrom(
      this.store.select(selectIsPersonalAccount).pipe(
        timeout(20000),
        catchError(error => {
          this.utilService.error(
            `[Auth] isPersonalAccount timeout or error. -- Error: ${getErrorMessage(error)}`,
            {
              error: formatErrorMessage(error, true)
            }
          );
          return of(false);
        })
      )
    );
    return personalAccount;
  }

  private async getTokens() {
    this.utilService.log('[Auth] Inside getTokens');
    try {
      let tokens = await TokenStorage.get();
      if (!tokens) {
        this.utilService.log('[Auth] No existing tokens');
      }
      let isExpired = true;
      const tokenExpiry = (tokens as OAuth2Tokens)?.tokenExpiry;
      if (tokenExpiry) {
        const tokenExpiryDate = new Date(tokenExpiry);
        this.utilService.log(`[Auth] Found existing tokens with expiry date -- ${tokenExpiryDate}`);
        isExpired = new Date() > tokenExpiryDate;

        if (isExpired) {
          this.utilService.error(
            `[Auth] The token is expired! -- Current Date : ${new Date().toString()} || Token Expiration Date: ${tokenExpiryDate}`
          );
        }
      }
      if (!!(tokens as OAuth2Tokens)?.accessToken && !isExpired) {
        this.utilService.log(`[Auth] Token is VALID!`);
        return tokens;
      } else {
        this.utilService.log(
          `[Auth] No existing valid tokens trying to fetch tokens from forgerock`
        );
        tokens = await this.forgerockAuthService.getTokens();
        if (tokens) {
          this.utilService.log(
            `[Auth] VALID TOKENS from forgerock, expiration on -- ${new Date((tokens as OAuth2Tokens).tokenExpiry || '')}`
          );
          return tokens;
        } else {
          this.utilService.error('[Auth] Failed to fetch tokens from forgerock');
        }
      }
    } catch (error) {
      this.utilService.error(`[Auth] Error getting tokens. -- Error : ${getErrorMessage(error)}`, {
        error: formatErrorMessage(error, true)
      });
    }
    this.utilService.error('[Auth] No tokens');
    return null;
  }

  private validateToken() {
    this.utilService.log('[Auth] Inside validateToken');
    const emailTokenUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.EMAIL_TOKEN_URL);
    if (emailTokenUrl) {
      this.utilService.log('[Auth] Validate Token Flow !');
      localStorage.removeItem(LOCAL_STORAGE_KEYS.EMAIL_TOKEN_URL);
      this.store.dispatch(fromRouterActions.Go({ path: [emailTokenUrl] }));
    }
    return false;
  }

  private isValidateTokenFlow() {
    const emailTokenUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.EMAIL_TOKEN_URL);
    return !!emailTokenUrl;
  }

  private isPendingRedirectFlow() {
    const key = this.copyService.getCopy('auth.pendingRedirectKey');
    if (!key) return false;
    const pendingRedirect = localStorage.getItem(key);
    return !!pendingRedirect;
  }

  private goToPendingRedirect() {
    const key = this.copyService.getCopy('auth.pendingRedirectKey');
    if (!key) return false;
    const pendingRedirect = localStorage.getItem(key);
    if (pendingRedirect) {
      this.utilService.log(
        `[Auth] Deeplink Flow -- ${this.utilService.maskSensitiveParams(pendingRedirect)}`
      );
      localStorage.removeItem(key);
      // By url, not as a path command: a deep link can carry a query (e.g. DocuSign's
      // `?event=OnSigningComplete&envelopeId=…` return), which a command would encode into the path.
      this.router.navigateByUrl(pendingRedirect);
    }
    return false;
  }

  private async guard(url: string) {
    try {
      this.utilService.log(`[Auth] URL -- ${this.utilService.maskSensitiveParams(url)}`);
      if (localStorage.getItem(LOCAL_STORAGE_KEYS.LEFT_APP) === '1') {
        this.utilService.log(`[Auth] Return back -- ${this.utilService.maskSensitiveParams(url)}`);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LEFT_APP);
      }

      if (localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN) === '1') {
        this.utilService.log(
          `[Auth] Refresh token detected - Auth skipped -- ${this.utilService.maskSensitiveParams(url)}`
        );
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        return true;
      }
      this.forgerockAuthService.redirectUrl = url;
      const authenticated = await this.isAuthenticated();
      const parts = url.split('/');
      const pathName = parts.length > 1 ? parts[1] : '';
      const emailValidationFlow = this.checkFlow(pathName ?? '', 'validate-email');
      if (authenticated) {
        const personalAccount = await this.isPersonalAccount();
        const currentFlow: string | null = localStorage.getItem(LOCAL_STORAGE_KEYS.FR_FLOW_TYPE);
        const paperlessSignupFlow = this.checkFlow(currentFlow, 'enrollment');
        const changePassFlow = this.checkFlow(currentFlow, 'change-password');
        const validateTokenFlow = this.isValidateTokenFlow();
        const pendingRedirectFlow = this.isPendingRedirectFlow();
        if (changePassFlow) {
          return this.changePassword(url);
        } else if (paperlessSignupFlow) {
          return this.paperlessSignup(url, personalAccount);
        } else if (validateTokenFlow) {
          return this.validateToken();
        } else if (pendingRedirectFlow) {
          return this.goToPendingRedirect();
        }
        return true;
      } else {
        if (emailValidationFlow) return this.sendToEmailValidation(url);
        else if (this.isForgotPassFlow(url)) return this.handleForgotPass(url);
        else return this.sendToLogin(url);
      }
    } catch {
      return this.sendToLogin(url);
    }
  }

  private isForgotPassFlow(url: string) {
    const params = new URLSearchParams(window.location.search);
    return (
      (url === '/overview' || url.startsWith('/overview?') || url.startsWith('/overview/')) &&
      !params.has('error_description')
    );
  }

  private async handleForgotPass(url: string) {
    try {
      this.utilService.log(
        `[Auth] Forgot Password Flow -- ${this.utilService.maskSensitiveParams(url)}`
      );
      this.forgerockAuthService.redirectUrl = url;
      const acrValues = env.forgerock.login;
      const tokens = await this.forgerockAuthService.getTokens(acrValues, false);
      if (tokens) {
        // Wait for authentication to complete before allowing navigation
        return firstValueFrom(
          this.dispatchToAuthenticate().pipe(
            timeout(20000),
            catchError(error => {
              this.utilService.error(
                `[Auth] Authentication timeout in handleForgotPass. -- Error: ${getErrorMessage(error)} `,
                {
                  error: formatErrorMessage(error, true)
                }
              );
              this.spinner.stop();
              return of(false);
            })
          )
        );
      } else return this.sendToLogin(url);
    } catch {
      return this.sendToLogin(url);
    }
  }

  private sendToLogin(url: string): boolean {
    this.utilService.log(`[Auth] Login Flow -- ${this.utilService.maskSensitiveParams(url)}`);
    this.setPendingRedirect(url);
    this.forgerockAuthService.redirectUrl = url;
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/login']
      })
    );
    return false;
  }
  private sendToEmailValidation(url: string): boolean {
    this.utilService.log(
      `[Auth] Email Validation Flow -- ${this.utilService.maskSensitiveParams(url)}`
    );
    this.forgerockAuthService.redirectUrl = url;

    localStorage.setItem(LOCAL_STORAGE_KEYS.EMAIL_TOKEN_URL, url);

    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/email-validation']
      })
    );
    return false;
  }

  private dispatchToAuthenticate(): Observable<boolean> {
    this.analyticsFacade.trackEvent({
      event: 'authenticate_sign_in_start'
    });
    this.spinner.start();
    this.store.dispatch(forgerockLogin());

    // Listen for either success or failure actions
    return this.actions$.pipe(
      ofType(loginUserSuccess, loginUserFail),
      take(1),
      map(action => {
        this.utilService.log(`[Auth] Login -- ${action.type}`);
        this.spinner.stop();
        if (action.type === loginUserSuccess.type) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  private setPendingRedirect(url: string) {
    this.utilService.log(
      `[Auth] Set pending redirect -- ${this.utilService.maskSensitiveParams(url)}`
    );
    const deepLinks: string[] = this.copyService.getCopy('auth.deepLinks') || [];
    const key = this.copyService.getCopy('auth.pendingRedirectKey');
    if (!key) {
      this.utilService.warn(
        `[Auth] Missing auth.pendingRedirectKey in copy service -- ${this.utilService.maskSensitiveParams(url)}`
      );
      return;
    }
    const isDeepLink = deepLinks.some(deepLink => url.includes(deepLink));
    if (isDeepLink) {
      localStorage.setItem(key, url);
    }
  }

  private sendToApp(event: unknown) {
    if (['int', 'int01'].includes(env.environment)) {
      this.componentViewService.sendToApp(event);
    }
  }
}

/**
 * Route entry point. The 500-plus lines of flow handling above stay in an injectable service
 * rather than being flattened into this function
 */
export const forgerockAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => inject(ForgerockAuthGuardService).canActivateRoute(route, state);
